// The live socket every app uses to redraw without being closed and reopened (PMU-202). A WebView
// cannot put a bearer token on a WebSocket, so each connection first trades the session for a
// one-use ticket; staff get site- or room-scoped messages, guardians only "changed" for their
// own children. Whatever arrives, the screens refetch through the normal API.
import { useEffect, useRef, useState } from "react";
import { api } from "./api.ts";

export const liveUrl = (apiBase: string, siteId: string, ticket: string, origin = typeof location === "undefined" ? "" : location.origin) =>
  `${(apiBase || origin).replace(/^http/, "ws")}/api/v1/sites/${encodeURIComponent(siteId)}/live?ticket=${encodeURIComponent(ticket)}`;

/** A module's message on the site's live socket (`module` names it), e.g. a new chat message. */
export type ModuleLiveMessage = { type: string; module: string } & Record<string, unknown>;
const moduleListeners = new Set<(m: ModuleLiveMessage) => void>();
/** Hears modules' messages from whichever live socket the app has open; returns the unsubscribe. */
export function onModuleLive(fn: (m: ModuleLiveMessage) => void) { moduleListeners.add(fn); return () => { moduleListeners.delete(fn); }; }
/** Hands a module's message from any live socket (the apps', the console's) to its listeners. */
export function emitModuleLive(m: ModuleLiveMessage) { for (const l of moduleListeners) l(m); }

/** A live message; staff "event" messages carry the sealed event and names for an alert. */
export interface LiveMessage {
  type: string;
  child_id?: string;
  event?: { type: string; child_id: string | null; server_ts: number; server_flags?: string[]; pre?: import("@vavr/core").PreImage };
  names?: { child: string | null; adult: string | null };
}

/** Reconnect delay: 2 s, 4 s, 8 s … capped at a minute. */
export const backoff = (attempt: number) => Math.min(60_000, 2_000 * 2 ** Math.max(0, attempt));

/** While the socket is down, screens still refresh this often. */
export const FALLBACK_POLL_MS = 60_000;

/**
 * Calls `onChange` for every message, when the socket (re)connects, whenever the app comes back to
 * the foreground (a background WebView may have been frozen with its socket still "open"), and on
 * a slow poll while disconnected. Returns whether the socket is connected.
 */
export function useLiveSocket(o: { siteId: string | null; apiBase: string; ticketPath: (siteId: string) => string; onChange: (message?: LiveMessage) => void }): boolean {
  const [connected, setConnected] = useState(false);
  const cb = useRef(o.onChange);
  cb.current = o.onChange;
  const ticketPath = useRef(o.ticketPath);
  ticketPath.current = o.ticketPath;
  const { siteId, apiBase } = o;
  useEffect(() => {
    if (!siteId || typeof WebSocket === "undefined") return;
    let ws: WebSocket | null = null;
    let stopped = false;
    let attempt = 0;
    let retry: ReturnType<typeof setTimeout> | undefined;
    let ping: ReturnType<typeof setInterval> | undefined;
    const connect = async () => {
      if (stopped) return;
      try {
        const { ticket } = await api<{ ticket: string }>("POST", ticketPath.current(siteId));
        if (stopped) return;
        ws = new WebSocket(liveUrl(apiBase, siteId, ticket));
        ws.onopen = () => { attempt = 0; setConnected(true); cb.current(); };
        ws.onmessage = (m) => {
          if (m.data === "pong") return;
          let msg: LiveMessage | undefined;
          try { msg = JSON.parse(String(m.data)) as LiveMessage; } catch { msg = undefined; }
          // A module's own message (e.g. a chat message) goes to that module, not to the board.
          if (msg && (msg as { module?: string }).module) { emitModuleLive(msg as ModuleLiveMessage); return; }
          cb.current(msg);
        };
        ws.onclose = () => { setConnected(false); clearInterval(ping); schedule(); };
        // Keeps idle proxies and carrier NAT from dropping the socket; the runtime answers "pong"
        // without waking the site Durable Object (setWebSocketAutoResponse).
        ping = setInterval(() => { if (ws?.readyState === WebSocket.OPEN) ws.send("ping"); }, 45_000);
      } catch { schedule(); }
    };
    const schedule = () => { if (!stopped) { clearTimeout(retry); retry = setTimeout(connect, backoff(attempt++)); } };
    const wake = () => {
      if (document.visibilityState !== "visible") return;
      cb.current();
      if (!ws || ws.readyState === WebSocket.CLOSED) { attempt = 0; clearTimeout(retry); void connect(); }
    };
    const poll = setInterval(() => { if (ws?.readyState !== WebSocket.OPEN && document.visibilityState === "visible") cb.current(); }, FALLBACK_POLL_MS);
    document.addEventListener("visibilitychange", wake);
    void connect();
    return () => { stopped = true; clearTimeout(retry); clearInterval(ping); clearInterval(poll); document.removeEventListener("visibilitychange", wake); ws?.close(); };
  }, [siteId, apiBase]);
  return connected;
}
